import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.css';
import { CircularProgress } from '@mui/material';
import { useAuthFileUrl } from '../../../../components/patient-attachments/AuthFile';

export const MIN_SCALE = 1;
export const MAX_SCALE = 6;
export const DEFAULT_TRANSFORM = Object.freeze({ scale: 1, x: 0, y: 0 });

/** Próximo zoom por passo (botões) ou pela roda do mouse. */
export function zoomTransform(transform, factor) {
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, transform.scale * factor));
  if (scale === MIN_SCALE) return DEFAULT_TRANSFORM;
  return { ...transform, scale };
}

/**
 * Uma foto no palco do comparador, só visualização: a transformação (zoom e
 * deslocamento) é controlada pelo pai para os dois lados andarem juntos.
 * Arrastar move, roda do mouse aproxima; o arquivo nunca é alterado.
 *
 * `children` são sobreposições do palco (divisor do deslizante, etiquetas).
 */
const ZoomableImage = ({ attachment, transform, onTransform, children }) => {
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const { url, loading } = useAuthFileUrl(attachment ? attachment.id : null, 'full', Boolean(attachment));

  // Roda do mouse: precisa ser listener não passivo para impedir o scroll da página
  useEffect(() => {
    const node = stageRef.current;
    if (!node || !onTransform) return undefined;
    const handleWheel = (event) => {
      event.preventDefault();
      onTransform((current) => zoomTransform(current, event.deltaY < 0 ? 1.15 : 1 / 1.15));
    };
    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => node.removeEventListener('wheel', handleWheel);
  }, [onTransform]);

  function handlePointerDown(event) {
    if (!onTransform || event.button !== 0) return;
    dragRef.current = { startX: event.clientX, startY: event.clientY, x: transform.x, y: transform.y };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  }

  function handlePointerMove(event) {
    const drag = dragRef.current;
    if (!drag) return;
    const x = drag.x + (event.clientX - drag.startX);
    const y = drag.y + (event.clientY - drag.startY);
    onTransform((current) => ({ ...current, x, y }));
  }

  function handlePointerUp(event) {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  const { scale, x, y } = transform || DEFAULT_TRANSFORM;

  return (
    <div
      ref={stageRef}
      className={`${styles.stage} ${dragging ? styles.stageDragging : ''}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {url && (
        <img
          className={styles.stageImage}
          src={url}
          alt={attachment ? (attachment.title || 'Foto de evolução') : ''}
          style={{ transform: `translate(${x}px, ${y}px) scale(${scale})` }}
          draggable={false}
        />
      )}
      {loading && (
        <div className={styles.stageLoading}>
          <CircularProgress size={28} color="inherit" />
        </div>
      )}
      {children}
    </div>
  );
};

export default ZoomableImage;
